// src/components/AudioPlayer.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Marquee from "react-fast-marquee";
import ClientOnly from './ClientOnly';
import {
    AudioPlayerWrapper,
    TrackList,
    TrackItem,
    TrackTitleSpan,
    TrackArtistSpan,
    ControlsWrapper,
    PlayPauseButton,
    TrackDisplayContainer,
    TimeDisplay,
    ProgressBarContainer,
    ProgressBarFill
} from './ui/PageElements';

// Helper function to format time
const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) {
        return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
};

// Global audio data object for shader consumption
// This allows SpacetimeGrid to read audio levels without prop drilling
if (typeof window !== 'undefined') {
    window.audioData = {
        // Immediate (raw) values - reactive, for punchy effects
        amplitude: 0,
        bass: 0,
        mid: 0,
        treble: 0,
        // Attenuated (smoothed) values - for gradual/flow effects
        ampAtt: 0,
        bassAtt: 0,
        midAtt: 0,
        trebleAtt: 0,
        // Beat detection flags (0 or 1, with decay)
        beatAmplitude: 0,
        beatBass: 0,
        beatMid: 0,
        beatTreble: 0,
        // State
        isPlaying: false
    };
}

// History buffer class for beat detection
class AudioHistory {
    constructor(size = 43) { // ~1 second at 60fps
        this.size = size;
        this.buffer = [];
        this.sum = 0;
        this.sumSquares = 0;
    }

    push(value) {
        this.buffer.push(value);
        this.sum += value;
        this.sumSquares += value * value;

        if (this.buffer.length > this.size) {
            const removed = this.buffer.shift();
            this.sum -= removed;
            this.sumSquares -= removed * removed;
        }
    }

    get average() {
        if (this.buffer.length === 0) return 0;
        return this.sum / this.buffer.length;
    }

    get variance() {
        if (this.buffer.length < 2) return 0;
        const avg = this.average;
        return (this.sumSquares / this.buffer.length) - (avg * avg);
    }

    get length() {
        return this.buffer.length;
    }
}

function AudioPlayer({ tracks = [] }) {
    const [currentTrackIndex, setCurrentTrackIndex] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState(null);
    const [isTextOverflowing, setIsTextOverflowing] = useState(false); // State for overflow status

    const audioRef = useRef(null);
    const progressBarRef = useRef(null);
    const trackDisplayContainerRef = useRef(null);
    // Need a ref for the *content inside* the marquee to measure it
    const marqueeContentRef = useRef(null);

    // Web Audio API refs for audio-reactive visuals
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const animationFrameRef = useRef(null);
    const dataArrayRef = useRef(null);

    // History buffers for beat detection (one per frequency band)
    const historyRef = useRef({
        amplitude: new AudioHistory(43),
        bass: new AudioHistory(43),
        mid: new AudioHistory(43),
        treble: new AudioHistory(43)
    });

    // Attenuated values for smooth visualization
    const attenuatedRef = useRef({
        amplitude: 0,
        bass: 0,
        mid: 0,
        treble: 0
    });

    const currentTrack = currentTrackIndex !== null ? tracks[currentTrackIndex] : null;
    // Calculate the full text content ONCE here
    const fullDisplayText = currentTrack
        ? `Now Playing: ${currentTrack.title}${currentTrack.artist ? ` - ${currentTrack.artist}` : ''}`
        : 'Select a track';

    // --- Handlers ---
    const handleTrackClick = useCallback((index) => {
        if (index === currentTrackIndex && audioRef.current?.src) {
            togglePlayPause();
        } else {
            setCurrentTrackIndex(index);
            // Reset states for new track load
            setIsPlaying(false);
            setError(null);
            setCurrentTime(0); // Reset time display
            setDuration(0); // Reset duration display
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTrackIndex, tracks]); // Keep togglePlayPause out

    const handleTrackKeyDown = useCallback((event, index) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleTrackClick(index);
        }
    }, [handleTrackClick]);

    const togglePlayPause = useCallback(() => {
        if (!audioRef.current) return;
        if (currentTrackIndex === null && tracks.length > 0) {
            // If no track is selected, start playing the first one
            handleTrackClick(0);
            return;
        }
        const audio = audioRef.current;
        if (isPlaying) {
            audio.pause();
        } else {
            // Only attempt play if ready
            if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or more
                audio.play().catch(err => {
                    setError("Playback failed. Browser may require user interaction.");
                    setIsPlaying(false); // Reflect failure in state
                });
            } else if (audio.src) {
                // If src is set but not ready, try loading again
                audio.load(); // Attempt to buffer
            }
        }
        // Actual state update will happen via 'play'/'pause' event listeners
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying, currentTrackIndex, tracks, handleTrackClick]);

    const handleTimeUpdate = useCallback(() => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    }, []);

    const handleLoadedMetadata = useCallback(() => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    }, []);

    const handleTrackEnd = useCallback(() => {
        // Advance to next track, loop back to start if at end
        if (currentTrackIndex !== null && tracks.length > 0) {
            const nextIndex = (currentTrackIndex + 1) % tracks.length;
            setCurrentTrackIndex(nextIndex);
            // Reset for new track
            setCurrentTime(0);
            setDuration(0);
            // Note: The useEffect watching currentTrackIndex will handle loading and playing
        } else {
            setIsPlaying(false);
        }
    }, [currentTrackIndex, tracks.length]);

    const handleProgressClick = useCallback((event) => {
        if (!audioRef.current || !progressBarRef.current || isNaN(duration) || duration <= 0) return;
        const progressBar = progressBarRef.current;
        const rect = progressBar.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const width = progressBar.offsetWidth;
        const percentage = Math.max(0, Math.min(1, clickX / width));
        const newTime = duration * percentage;
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime); // Update state immediately for responsiveness
    }, [duration]);

    const handleProgressKeyDown = useCallback((event) => {
        if (!audioRef.current || isNaN(duration) || duration <= 0) return;

        let timeChange = 0;
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            timeChange = 5; // Skip forward 5 seconds
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            timeChange = -5; // Skip backward 5 seconds
        } else if (event.key === 'Home') {
            audioRef.current.currentTime = 0;
            setCurrentTime(0);
            return;
        } else if (event.key === 'End') {
            audioRef.current.currentTime = duration;
            setCurrentTime(duration);
            return;
        } else {
            return; // Not a key we handle
        }

        event.preventDefault();
        const newTime = Math.max(0, Math.min(duration, currentTime + timeChange));
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    }, [duration, currentTime]);

    const handleAudioError = useCallback((e) => {
        // Ignore abort errors which can happen on src change
        if (audioRef.current?.error?.code === MediaError.MEDIA_ERR_ABORTED) {
            return;
        }
        setError(`Audio error: ${audioRef.current?.error?.message || 'Unknown error'}`);
        setIsPlaying(false);
    }, []);


    // --- Effects ---
    // Effect to load/play audio when currentTrackIndex changes
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        if (currentTrackIndex !== null) {
            const track = tracks[currentTrackIndex];
            // Only change src if it's actually different
            if (track && audio.src !== track.src) {
                audio.src = track.src;
                audio.load(); // Important for new src
                // Play attempt - may be blocked by browser initially
                audio.play()
                    .then(() => {
                        // State is set by 'play' listener
                    })
                    .catch(err => {
                        setIsPlaying(false); // Ensure state is correct if autoplay fails
                    });
            }
        } else {
            // No track selected, clear src and pause
            if (audio.src) {
                audio.pause();
                audio.removeAttribute('src');
                audio.load(); // Reset internal state
                setIsPlaying(false);
                setCurrentTime(0);
                setDuration(0);
            }
        }
    }, [currentTrackIndex, tracks]);


    // Effect to attach/detach core audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            const syncPlayState = () => setIsPlaying(!audio.paused);

            audio.addEventListener('timeupdate', handleTimeUpdate);
            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
            audio.addEventListener('ended', handleTrackEnd);
            audio.addEventListener('error', handleAudioError);
            audio.addEventListener('play', syncPlayState);
            audio.addEventListener('pause', syncPlayState);

            // Cleanup
            return () => {
                audio.removeEventListener('timeupdate', handleTimeUpdate);
                audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                audio.removeEventListener('ended', handleTrackEnd);
                audio.removeEventListener('error', handleAudioError);
                audio.removeEventListener('play', syncPlayState);
                audio.removeEventListener('pause', syncPlayState);
            };
        }
    }, [handleTimeUpdate, handleLoadedMetadata, handleTrackEnd, handleAudioError]);


    // --- Effect for Web Audio API setup and audio analysis ---
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || typeof window === 'undefined') return;

        // Function to initialize Web Audio API (called on first play)
        const initAudioContext = () => {
            if (audioContextRef.current) return; // Already initialized

            try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                const audioContext = new AudioContext();
                audioContextRef.current = audioContext;

                // Create analyser node
                const analyser = audioContext.createAnalyser();
                analyser.fftSize = 256; // Gives us 128 frequency bins
                analyser.smoothingTimeConstant = 0.4; // Lower = more responsive to transients
                analyserRef.current = analyser;

                // Create source from audio element
                const source = audioContext.createMediaElementSource(audio);
                sourceRef.current = source;

                // Connect: source -> analyser -> destination
                source.connect(analyser);
                analyser.connect(audioContext.destination);

                // Create data array for frequency data
                dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);
            } catch (err) {
                console.warn('Web Audio API initialization failed:', err);
            }
        };

        // Function to analyze audio and update global data
        const analyzeAudio = () => {
            if (!analyserRef.current || !dataArrayRef.current) {
                animationFrameRef.current = requestAnimationFrame(analyzeAudio);
                return;
            }

            const analyser = analyserRef.current;
            const dataArray = dataArrayRef.current;
            const history = historyRef.current;
            const attenuated = attenuatedRef.current;

            // Get frequency data
            analyser.getByteFrequencyData(dataArray);

            // Calculate frequency bands
            const bufferLength = analyser.frequencyBinCount; // 128 bins

            // Frequency bin calculation for 44100Hz sample rate, 256 FFT size:
            // Each bin = 44100 / 256 ≈ 172Hz
            // Bass (20-250Hz): bins 0-1 (but we'll use 0-3 for more data)
            // Mid (250-4000Hz): bins 1-23
            // Treble (4000-20000Hz): bins 23-128

            let bassSum = 0, midSum = 0, trebleSum = 0, totalSum = 0;
            let bassCount = 0, midCount = 0, trebleCount = 0;

            for (let i = 0; i < bufferLength; i++) {
                const value = dataArray[i];
                totalSum += value;

                if (i < 4) {
                    // Bass: ~0-688Hz
                    bassSum += value;
                    bassCount++;
                } else if (i < 24) {
                    // Mid: ~688-4128Hz
                    midSum += value;
                    midCount++;
                } else {
                    // Treble: 4128Hz+
                    trebleSum += value;
                    trebleCount++;
                }
            }

            // Normalize to 0-1 range (max value is 255)
            const amplitude = totalSum / (bufferLength * 255);
            const bass = bassSum / (bassCount * 255);
            const mid = midSum / (midCount * 255);
            const treble = trebleSum / (trebleCount * 255);

            // Push to history buffers
            history.amplitude.push(amplitude);
            history.bass.push(bass);
            history.mid.push(mid);
            history.treble.push(treble);

            // Beat detection function using dynamic threshold
            // threshold = C - k * variance (higher variance = lower threshold)
            const detectBeat = (instant, hist) => {
                if (hist.length < 10) return 0; // Need enough history

                const avg = hist.average;
                const variance = hist.variance;

                // Dynamic threshold: base of 1.4, reduced by variance
                // Clamp variance contribution to prevent negative threshold
                const C = 1.4;
                const k = 15;
                const threshold = Math.max(1.1, C - k * variance);

                // Beat detected if instant energy exceeds threshold * average
                if (instant > avg * threshold && avg > 0.01) {
                    // Return beat intensity (how much it exceeded threshold)
                    return Math.min(1.0, (instant - avg * threshold) / avg + 0.5);
                }
                return 0;
            };

            // Detect beats for each band
            const beatAmplitude = detectBeat(amplitude, history.amplitude);
            const beatBass = detectBeat(bass, history.bass);
            const beatMid = detectBeat(mid, history.mid);
            const beatTreble = detectBeat(treble, history.treble);

            // Calculate attenuated values (asymmetric smoothing like MilkDrop)
            // Fast attack (0.2), slower release (0.5)
            const updateAttenuated = (current, target) => {
                const rate = target > current ? 0.3 : 0.1;
                return current + (target - current) * rate;
            };

            attenuated.amplitude = updateAttenuated(attenuated.amplitude, amplitude);
            attenuated.bass = updateAttenuated(attenuated.bass, bass);
            attenuated.mid = updateAttenuated(attenuated.mid, mid);
            attenuated.treble = updateAttenuated(attenuated.treble, treble);

            // Update global audio data
            if (window.audioData) {
                // Immediate values
                window.audioData.amplitude = amplitude;
                window.audioData.bass = bass;
                window.audioData.mid = mid;
                window.audioData.treble = treble;

                // Attenuated values
                window.audioData.ampAtt = attenuated.amplitude;
                window.audioData.bassAtt = attenuated.bass;
                window.audioData.midAtt = attenuated.mid;
                window.audioData.trebleAtt = attenuated.treble;

                // Beat detection (decay existing beats, replace if new beat is stronger)
                // Smoother decay (0.75 instead of 0.85) for less harsh transients
                const beatDecay = 0.75;
                // Also smooth incoming beats slightly (don't jump straight to full value)
                const beatSmooth = 0.6;
                const smoothBeat = (newBeat, current) => {
                    const target = Math.max(newBeat, current * beatDecay);
                    return current + (target - current) * beatSmooth;
                };
                window.audioData.beatAmplitude = smoothBeat(beatAmplitude, window.audioData.beatAmplitude);
                window.audioData.beatBass = smoothBeat(beatBass, window.audioData.beatBass);
                window.audioData.beatMid = smoothBeat(beatMid, window.audioData.beatMid);
                window.audioData.beatTreble = smoothBeat(beatTreble, window.audioData.beatTreble);

                window.audioData.isPlaying = !audio.paused;
            }

            animationFrameRef.current = requestAnimationFrame(analyzeAudio);
        };

        // Start analysis when playing
        const handlePlay = () => {
            initAudioContext();

            // Resume audio context if suspended (browser autoplay policy)
            if (audioContextRef.current?.state === 'suspended') {
                audioContextRef.current.resume();
            }

            // Start analysis loop if not already running
            if (!animationFrameRef.current) {
                analyzeAudio();
            }
        };

        // Reset audio data when paused
        const handlePause = () => {
            if (window.audioData) {
                // Gradually fade out (will be handled by shader smoothing)
                window.audioData.isPlaying = false;
            }
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);

        // Cleanup
        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            // Reset global audio data
            if (window.audioData) {
                window.audioData.amplitude = 0;
                window.audioData.bass = 0;
                window.audioData.mid = 0;
                window.audioData.treble = 0;
                window.audioData.ampAtt = 0;
                window.audioData.bassAtt = 0;
                window.audioData.midAtt = 0;
                window.audioData.trebleAtt = 0;
                window.audioData.beatAmplitude = 0;
                window.audioData.beatBass = 0;
                window.audioData.beatMid = 0;
                window.audioData.beatTreble = 0;
                window.audioData.isPlaying = false;
            }

            // Reset history buffers
            if (historyRef.current) {
                historyRef.current.amplitude = new AudioHistory(43);
                historyRef.current.bass = new AudioHistory(43);
                historyRef.current.mid = new AudioHistory(43);
                historyRef.current.treble = new AudioHistory(43);
            }

            // Note: We don't close the AudioContext here because it can't be reused
            // The browser will clean it up when the page unloads
        };
    }, []);


    // --- Effect for Overflow Detection ---
    useEffect(() => {
        const container = trackDisplayContainerRef.current;

        // Function to check overflow using a temporary element
        const checkOverflow = () => {
            if (container && fullDisplayText && fullDisplayText !== 'Select a track') {
                // Create a temporary span, styled identically to Marquee content
                const measureSpan = document.createElement('span');
                // Apply styles critical for width calculation
                measureSpan.style.fontSize = '13px'; // Match Marquee style
                measureSpan.style.fontStyle = 'italic'; // Match Marquee style
                measureSpan.style.whiteSpace = 'nowrap'; // CRUCIAL
                measureSpan.style.visibility = 'hidden'; // Invisible
                measureSpan.style.position = 'absolute'; // Out of layout flow
                measureSpan.style.fontFamily = "'JetBrains Mono', monospace"; // Match font
                measureSpan.textContent = fullDisplayText; // Use the *single* copy of text

                // Append to body to allow measurement, measure, then remove
                document.body.appendChild(measureSpan);
                const textWidth = measureSpan.offsetWidth; // Use offsetWidth for rendered width
                document.body.removeChild(measureSpan);

                const containerWidth = container.clientWidth;
                const isOverflow = textWidth > containerWidth;

                // Update state only if it changes
                setIsTextOverflowing(isOverflow);
            } else {
                // If container/text isn't ready or no track selected, default to not overflowing
                setIsTextOverflowing(false);
            }
        };

        // Check initially after a short delay to allow container rendering
        const timerId = setTimeout(checkOverflow, 150); // Slightly longer delay maybe

        // --- Use ResizeObserver for dynamic checks ---
        let resizeObserver;
        if (container) {
            resizeObserver = new ResizeObserver(checkOverflow); // Re-check on resize
            resizeObserver.observe(container);
        }

        // Cleanup
        return () => {
            clearTimeout(timerId);
            if (resizeObserver && container) {
                resizeObserver.unobserve(container);
            }
        };
        // Re-run when the text content changes, or container ref potentially updates
    }, [fullDisplayText]);


    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

    // --- MARQUEE CONTENT WITH GAP ---
    // Define the gap more explicitly for clarity
    const marqueeGap = "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"; // Series of non-breaking spaces
    const marqueeContent = `${fullDisplayText}${marqueeGap}`; // Text + Gap

    return (
        <AudioPlayerWrapper>
            {error && <div style={{ color: 'var(--primary-color)', marginBottom: '10px', fontSize: '12px' }}>Error: {error}</div>}
            <TrackList role="group" aria-label="Music tracks">
                {tracks.map((track, index) => (
                    <TrackItem
                        key={index}
                        $isActive={index === currentTrackIndex}
                        className="track-container"
                    >
                        <button
                            onClick={() => handleTrackClick(index)}
                            onKeyDown={(e) => handleTrackKeyDown(e, index)}
                            aria-pressed={index === currentTrackIndex}
                            aria-label={`Play ${track.title}${track.artist && !track.artistUrl ? ` by ${track.artist}` : ''}`}
                            className="track-play-button"
                        >
                            <span className="track-icon"></span>
                            <TrackTitleSpan>{track.title}</TrackTitleSpan>
                            {track.artist && !track.artistUrl && (
                                <TrackArtistSpan>{track.artist}</TrackArtistSpan>
                            )}
                        </button>
                        {track.artist && track.artistUrl && (
                            <a
                                href={track.artistUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Visit ${track.artist}'s page (opens in new tab)`}
                                className="track-artist-link"
                            >
                                {track.artist}
                            </a>
                        )}
                    </TrackItem>
                ))}
            </TrackList>

            <ControlsWrapper>
                <PlayPauseButton
                    onClick={togglePlayPause}
                    disabled={!tracks || tracks.length === 0 || !!error}
                    aria-label={isPlaying ? "Pause" : "Play"}
                    aria-pressed={isPlaying}
                >
                    {isPlaying ? '❚❚' : '▶'}
                </PlayPauseButton>

                {/* Container for marquee logic */}
                <TrackDisplayContainer ref={trackDisplayContainerRef}>
                    <ClientOnly>
                        <Marquee
                            // Use currentTrackIndex as key to force remount on track change
                            key={currentTrackIndex !== null ? tracks[currentTrackIndex]?.src : 'no-track'}
                            play={isTextOverflowing} // <-- Play ONLY if overflowing
                            pauseOnHover={true}
                            gradient={false}
                            speed={25}
                            style={{
                                fontSize: '13px',
                                color: '#cccccc',
                                fontStyle: 'italic',
                                width: '100%' // Let Marquee handle its internal width
                            }}
                        >
                            <span ref={marqueeContentRef}>
                                {/* Content rendered inside Marquee */}
                                {marqueeContent}
                            </span>
                        </Marquee>
                    </ClientOnly>
                </TrackDisplayContainer>

                <ProgressBarContainer
                    ref={progressBarRef}
                    onClick={handleProgressClick}
                    onKeyDown={handleProgressKeyDown}
                    role="slider"
                    tabIndex={0}
                    aria-label="Seek audio position"
                    aria-valuenow={Math.floor(currentTime)}
                    aria-valuemin={0}
                    aria-valuemax={Math.floor(duration) || 0}
                    aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
                >
                    <ProgressBarFill progress={progressPercent} />
                </ProgressBarContainer>
                <TimeDisplay>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </TimeDisplay>
            </ControlsWrapper>

            {/* Hidden HTML Audio Element */}
            <audio ref={audioRef} preload="metadata"></audio>

        </AudioPlayerWrapper>
    );
}

export default AudioPlayer;